import { useCallback, useContext, useState } from 'react'
import type { ComponentPropsWithoutRef } from 'react'
import { Download, Maximize, X } from 'lucide-react'
import { toast } from 'sonner'
import { StreamdownContext } from 'streamdown'
import type { ExtraProps } from 'streamdown'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

const ACTION_CLASS =
  'size-auto rounded-none p-1 text-muted-foreground hover:bg-transparent hover:text-foreground'

/** The stored filename, which already carries the diagram's name. */
function filenameFor(src: string) {
  const [path = ''] = src.split('?')
  const name = path.slice(path.lastIndexOf('/') + 1)
  return /\.[a-z0-9]{2,4}$/i.test(name) ? name : 'diagram.png'
}

async function download(src: string) {
  // Same shape as streamdown's own image download: fetch the bytes so the
  // filename survives (a signed URL is cross-origin, and `<a download>` is
  // ignored across origins), and fall back to a new tab if that is refused.
  try {
    const blob = await (await fetch(src)).blob()
    const href = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = href
    link.download = filenameFor(src)
    link.click()
    URL.revokeObjectURL(href)
  } catch {
    window.open(src, '_blank', 'noopener')
    toast.error('Could not download the image; opened it in a new tab')
  }
}

/**
 * Image renderer for chat markdown, replacing streamdown's built-in.
 *
 * The built-in floats a download button over the picture and dims it behind a
 * scrim on hover; both are gated on `group-hover`, which fires from any
 * `.group` ancestor — and `Message` is one, so hovering the prose dimmed every
 * image in the reply. This frames the image the way the mermaid block and the
 * table wrapper are framed instead: a bordered card with the actions in a
 * header, nothing overlaying the picture.
 */
export function ChatMarkdownImage({
  src,
  alt,
  className,
  node: _node,
  ...props
}: ComponentPropsWithoutRef<'img'> & ExtraProps) {
  const { isAnimating } = useContext(StreamdownContext)
  const [failed, setFailed] = useState(false)
  const source = typeof src === 'string' ? src : undefined

  const onDownload = useCallback(() => {
    if (source) void download(source)
  }, [source])

  if (!source) return null

  const label = alt?.trim() || 'diagram'
  const image = (
    <img
      alt={alt ?? ''}
      className={cn('max-w-full rounded-md', className)}
      onError={() => setFailed(true)}
      src={source}
      {...props}
    />
  )

  return (
    <div
      className="my-4 flex flex-col gap-2 rounded-lg border border-border bg-sidebar p-2"
      data-streamdown="image-block">
      <div className="flex h-8 items-center justify-between gap-2">
        <span className="ml-1 truncate font-mono text-muted-foreground text-xs lowercase">
          {label}
        </span>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            aria-label="Download image"
            className={ACTION_CLASS}
            disabled={isAnimating || failed}
            onClick={onDownload}
            size="icon-sm"
            title="Download image"
            variant="ghost">
            <Download />
          </Button>
          <Dialog>
            <DialogTrigger asChild>
              <Button
                aria-label="View fullscreen"
                className={ACTION_CLASS}
                disabled={isAnimating || failed}
                size="icon-sm"
                title="View fullscreen"
                variant="ghost">
                <Maximize />
              </Button>
            </DialogTrigger>
            <DialogContent
              aria-describedby={undefined}
              className="inset-0 top-0 left-0 flex max-w-none translate-x-0 translate-y-0 flex-col rounded-none p-0 sm:max-w-none"
              data-streamdown="image-fullscreen"
              showCloseButton={false}>
              <DialogTitle className="sr-only">{label}</DialogTitle>
              <div className="flex items-center justify-end gap-1 p-4">
                <Button
                  aria-label="Download image"
                  className={ACTION_CLASS}
                  onClick={onDownload}
                  size="icon-sm"
                  title="Download image"
                  variant="ghost">
                  <Download />
                </Button>
                <DialogClose asChild>
                  <Button
                    aria-label="Exit fullscreen"
                    className="size-auto rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                    size="icon-sm"
                    title="Exit fullscreen"
                    variant="ghost">
                    <X className="size-5" />
                  </Button>
                </DialogClose>
              </div>
              <div className="flex flex-1 items-center justify-center overflow-auto p-4 pt-0">
                <img
                  alt={alt ?? ''}
                  className="max-h-full max-w-full object-contain"
                  src={source}
                />
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="flex justify-center rounded-md border border-border bg-background p-2">
        {failed ? (
          // A diagram URL is signed and expires; say so rather than leaving a
          // broken-image glyph in the reply.
          <span className="py-6 text-muted-foreground text-xs italic">
            This image is no longer available
          </span>
        ) : (
          image
        )}
      </div>
    </div>
  )
}
